import React from 'react';

import { DynamicAudienceSection } from '@occc-app/routes/Audience/constants';

export type AudienceSectionRefsContextValue = {
  audienceSectionsRef: React.MutableRefObject<
    Partial<Record<DynamicAudienceSection, HTMLDivElement | null>>
  >;
  setSectionRef: (sectionName: DynamicAudienceSection, ref: HTMLDivElement | null) => void;
};

const defaultContextValue: AudienceSectionRefsContextValue = {
  audienceSectionsRef: { current: {} },
  setSectionRef: () => {},
};

const AudienceSectionRefsContext =
  React.createContext<AudienceSectionRefsContextValue>(defaultContextValue);

type AudienceSectionRefsProviderProps = {
  children?: React.ReactNode;
};

export const AudienceSectionRefsProvider = ({ children }: AudienceSectionRefsProviderProps) => {
  const audienceSectionsRef = React.useRef<
    Partial<Record<DynamicAudienceSection, HTMLDivElement | null>>
  >({});

  const setSectionRef = React.useCallback(
    (sectionName: DynamicAudienceSection, ref: HTMLDivElement | null) => {
      audienceSectionsRef.current = {
        ...audienceSectionsRef.current,
        [sectionName]: ref,
      };
    },
    [],
  );

  const value = React.useMemo(() => {
    return {
      audienceSectionsRef,
      setSectionRef,
    };
  }, [setSectionRef]);

  return (
    <AudienceSectionRefsContext.Provider value={value}>
      {children}
    </AudienceSectionRefsContext.Provider>
  );
};

export const useSectionRefs = () => {
  const contextValue = React.useContext(AudienceSectionRefsContext);
  return contextValue;
};
